import { FunctionComponent, useEffect, useState } from 'react'
import styled from 'styled-components'
import Tag from '../../../components/Tag'
import Text from '../../../components/Text'
import { styles } from '../../../styles/themes/style'
import { IPrice } from '../../../interfaces/models/billingPlan'
import { formatCurrency, formatPrice } from '../../../utils'
import Row from '../../../components/Row'

interface IPlanValueProps {
    isSelected?: boolean
    disabled?: boolean
}

interface ICycleToggleProps extends IPlanValueProps {
    selectedInterval?: string,
    prices: IPrice[],
    /** Not rendered, but still used to calculate the yearly saving */
    hiddenIntervals?: string[],
    onClickHandler?: (e: any) => void
}

export const calculateSaveAmount = (monthlyPrice?: IPrice, annualPrice?: IPrice): number => {
    if(monthlyPrice && annualPrice) {
        const annualMonthly = monthlyPrice?.price * 12
        return (annualMonthly - annualPrice?.price) / annualMonthly * 10000
    }
    return 0
}

const CycleToggle: FunctionComponent<ICycleToggleProps> = ({ selectedInterval, prices, hiddenIntervals, onClickHandler }) => {

    const plansInitialValues = [
        {
            plan: 'Yearly',
            key: 'year',
            currency: 'usd',
            price: 0,
            save: 'Save 30%'
        },
        {
            plan: 'Monthly',
            key: 'month',
            currency: 'usd',
            price: 0,
            save: ''
        }
    ]
    const [plansValues, setPlansValues] = useState<{
        plan: string,
        key: string,
        currency: string,
        price: number,
        save?: string,
        disabled?: boolean
    }[]> (plansInitialValues)

    useEffect(() => {
        // Group prices by productId and interval to handle multiple prices per product
        const pricesByProductAndInterval: { [key: string]: IPrice[] } = {};
        prices.forEach(price => {
            const key = `${price.productId}-${price.interval}`;
            if (!pricesByProductAndInterval[key]) {
                pricesByProductAndInterval[key] = [];
            }
            pricesByProductAndInterval[key].push(price);
        });

        // Filter to get only active prices
        const activePrices: IPrice[] = [];
        Object.values(pricesByProductAndInterval).forEach(productIntervalPrices => {
            if (productIntervalPrices.length === 1) {
                // If only one price for this product+interval, keep it (backward compatibility)
                activePrices.push(productIntervalPrices[0]);
            } else {
                // If multiple prices, filter by active === true
                const activeProductPrices = productIntervalPrices.filter(price => price.active === true);
                if (activeProductPrices.length > 0) {
                    activePrices.push(...activeProductPrices);
                } else {
                    // Fallback: if no active prices found, keep the first one
                    activePrices.push(productIntervalPrices[0]);
                }
            }
        });

        let monthlyPrice: IPrice | undefined
        let annualPrice: IPrice | undefined
        let save: number
        let newPlans = activePrices.map(price => {
            if(price.interval === 'month') monthlyPrice = price 
            if(price.interval === 'year') annualPrice = price 
            return {
                plan: price.interval,
                key: price.interval,
                price: price.price,
                currency: price.currency,
                disabled: false // Starter Monthly plan is now available
            }
        })
        save = calculateSaveAmount(monthlyPrice, annualPrice)
        newPlans = newPlans.map(plan => plan.key === 'year' && save > 0 ? {...plan, save: `Save 30%`} : {...plan})
        setPlansValues(newPlans)
    }, [prices])

    return (
        <Row gap='16px'>
            {plansValues.filter(value => !hiddenIntervals?.includes(value.key)).map((value, index) => (
                <PlanValue
                    key={index}
                    isSelected={selectedInterval === value.key}
                    onClick={() => !value.disabled && onClickHandler && onClickHandler({ plan: value.plan, key: value.key })}
                    disabled={value.disabled}
                >
                    <Text $heading5 $capitalize>{`Bill ${value.plan}ly`}</Text>
                    <Row gap="8px" alignItems>
                        <Row gap="2px" alignItems>
                            <Text $heading5>{`${formatCurrency(value?.currency ?? 'usd')}${formatPrice(value?.price ?? 0)}`}</Text>
                            <Text $paragraph3 $capitalizeAll>{value.key}</Text>
                        </Row>
                        {value?.save && <Tag $color={styles.colors.green600} title={value.save} />}
                    </Row>
                </PlanValue>
            ))}
        </Row>
    )
}
const PlanValue = styled.div<IPlanValueProps>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border: 1px solid ${({ isSelected }) => isSelected ? styles.colors.primary600 : styles.colors.black200};
    border-radius: 4px;
    gap: 8px;
    padding: 16px;
    cursor: pointer;
    min-width: 200px;
    opacity: ${({ disabled }) => disabled ? 0.5 : 1};
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    &:hover {
        opacity: 0.75;
    } 
`
export default CycleToggle